import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { STATUS_META, ENTITY_META } from "@/lib/constants";
import { mapSearchUrl } from "@/lib/address";
import {
  Navigation,
  Phone,
  Building2,
  MapPin,
  StickyNote,
  Hash,
  Pencil,
  IdCard,
} from "lucide-react";

function formatNoteDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

export default function MemberDetailDialog({
  member,
  onOpenChange,
  onEdit,
  canWrite = false,
}) {
  if (!member) return null;

  const meta = STATUS_META[member.status] || STATUS_META.Beyaz;
  const em = ENTITY_META[member.entity_type] || ENTITY_META.Firma;
  const phoneLines = (member.phone || "").split("\n").filter((l) => l.trim());
  const noteDate = formatNoteDate(member.notes_updated_at);

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="member-detail-dialog"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            {member.order_no != null && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider bg-neutral-950 dark:bg-neutral-100 text-white dark:text-neutral-950 px-2 py-0.5 rounded">
                <Hash className="h-2.5 w-2.5" />
                {member.order_no}
              </span>
            )}
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider ${meta.bg} ${meta.text} px-2 py-0.5 rounded`}
            >
              {meta.option || meta.short}
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider ${em.bg} ${em.text} px-2 py-0.5 rounded`}
              data-testid="detail-entity-type"
            >
              {em.long}
            </span>
          </div>
          <DialogTitle className="text-left leading-tight">
            {member.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {member.company && member.company !== member.name && (
            <div className="flex items-start gap-2 text-neutral-700 dark:text-neutral-300">
              <Building2 className="h-4 w-4 shrink-0 mt-0.5 text-neutral-400" />
              <span className="leading-snug">{member.company}</span>
            </div>
          )}

          {(member.member_no || member.trade_reg_no || member.committee) && (
            <div className="flex items-start gap-2 text-neutral-600 dark:text-neutral-400">
              <IdCard className="h-4 w-4 shrink-0 mt-0.5 text-neutral-400" />
              <div className="flex flex-col gap-0.5 text-xs leading-relaxed">
                {member.member_no && (
                  <span>
                    Üye sicil:{" "}
                    <span className="font-mono">{member.member_no}</span>
                  </span>
                )}
                {member.trade_reg_no && (
                  <span>
                    Ticaret sicil:{" "}
                    <span className="font-mono">{member.trade_reg_no}</span>
                  </span>
                )}
                {member.committee && <span>{member.committee}</span>}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <Phone className="h-4 w-4 shrink-0 mt-1 text-neutral-400" />
            <div className="flex flex-col gap-1 min-w-0 w-full">
              {phoneLines.length === 0 && (
                <span className="text-neutral-400 text-xs">
                  Telefon kaydı yok
                </span>
              )}
              {phoneLines.map((line, i) => {
                const [numPart, ...rest] = line.split(" - ");
                const digits = numPart.replace(/[^\d+]/g, "");
                const label = rest.join(" - ").trim();
                if (digits.length < 10) {
                  return (
                    <div
                      key={i}
                      className="text-xs text-neutral-500 dark:text-neutral-400"
                    >
                      {line}
                      <span className="ml-1 text-neutral-400">
                        (numara yok)
                      </span>
                    </div>
                  );
                }
                return (
                  <a
                    key={i}
                    href={`tel:${digits}`}
                    className="flex items-baseline gap-2 rounded px-1.5 py-1 -mx-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    data-testid={`detail-phone-${i}`}
                  >
                    <span className="font-mono text-[13px] text-neutral-900 dark:text-neutral-100 shrink-0">
                      {numPart}
                    </span>
                    {label && (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                        {label}
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          </div>

          <div className={`flex items-start gap-2 rounded-lg border ${em.border} ${em.bg} p-2.5`}>
            <span className="h-2 w-2 rounded-full shrink-0 mt-1" style={{ background: em.dot }} />
            <div className="min-w-0">
              <div className={`text-[11px] font-semibold ${em.text}`}>{em.long}</div>
              <div className="text-[11px] text-neutral-600 dark:text-neutral-400">{em.vote}</div>
            </div>
          </div>

          {member.address && (
            <div className="flex items-start gap-2 text-neutral-700 dark:text-neutral-300">
              <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-neutral-400" />
              <div className="min-w-0">
                <span className="text-xs leading-relaxed">{member.address}</span>
                <a
                  href={mapSearchUrl(member.address)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-neutral-900 dark:text-neutral-100 hover:underline"
                  data-testid="detail-directions"
                >
                  <Navigation className="h-3 w-3" />
                  Yol tarifi al
                </a>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <StickyNote className="h-4 w-4 shrink-0 mt-0.5 text-neutral-400" />
            <div className="flex flex-col gap-1 min-w-0 w-full">
              {member.notes ? (
                <>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap text-neutral-700 dark:text-neutral-300">
                    {member.notes}
                  </p>
                  {noteDate && (
                    <span className="text-[10px] font-mono text-neutral-400">
                      Son değişiklik: {noteDate}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-neutral-400">Not girilmemiş</span>
              )}
            </div>
          </div>
        </div>

        {canWrite && (
          <div className="pt-2">
            <Button
              className="w-full h-10"
              onClick={() => onEdit?.(member)}
              data-testid="detail-edit-btn"
            >
              <Pencil className="h-4 w-4 mr-1.5" />
              Düzenle
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
